"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

type StoryPanel = {
  eyebrow: string;
  label: string;
  titleLines: readonly string[];
  copyLines: readonly string[];
  image: {
    src: string;
    alt: string;
  };
  imagePosition: string;
};

type MotionaStoryCarouselProps = {
  panels: readonly StoryPanel[];
};

export function MotionaStoryCarousel({ panels }: MotionaStoryCarouselProps) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const updateSelection = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    updateSelection();
    carouselApi.on("reInit", updateSelection);
    carouselApi.on("select", updateSelection);

    return () => {
      carouselApi.off("reInit", updateSelection);
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  return (
    <section className="overflow-hidden border-y border-[#050505]/8 bg-[#fff7d6] px-4 py-16 text-[#050505] md:px-6 md:py-24">
      <div className="mx-auto max-w-[1180px]">
        <div className="mb-8 grid gap-5 md:mb-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <p className="text-sm font-black text-[#8c0000]">MOTIONA 회복 흐름</p>
            <h2 className="mt-3 max-w-2xl text-[2.2rem] font-black leading-[1.12] md:text-[4rem]">
              현재 상태에서
              <br />
              일상 회복까지
            </h2>
          </div>
          <div className="flex flex-col gap-5 lg:items-end">
            <div className="hidden gap-2 md:flex">
              <button
                type="button"
                onClick={() => carouselApi?.scrollPrev()}
                disabled={!canScrollPrev}
                className="inline-flex size-11 items-center justify-center rounded-full border border-[#050505]/16 bg-[#fffdf2] text-[#050505] shadow-[0_10px_24px_rgba(80,54,0,0.1)] transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-38 disabled:hover:translate-y-0"
                aria-label="이전 회복 단계 보기"
              >
                <ArrowLeft className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => carouselApi?.scrollNext()}
                disabled={!canScrollNext}
                className="inline-flex size-11 items-center justify-center rounded-full border border-[#050505]/16 bg-[#fffdf2] text-[#050505] shadow-[0_10px_24px_rgba(80,54,0,0.1)] transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-38 disabled:hover:translate-y-0"
                aria-label="다음 회복 단계 보기"
              >
                <ArrowRight className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "center",
            containScroll: "trimSnaps",
          }}
          aria-label="MOTIONA 5단계 회복 흐름"
        >
          <CarouselContent className="ml-0">
            {panels.map((panel, index) => (
              <CarouselItem
                key={panel.label}
                className="basis-full pl-0"
              >
                <article className="grid h-full overflow-hidden rounded-[8px] border border-[#050505]/12 bg-[#fffdf2] shadow-[0_18px_44px_rgba(80,54,0,0.12)] md:grid-cols-[0.92fr_1.08fr]">
                  <figure className="relative min-h-[240px] overflow-hidden bg-[#fff0a8] md:min-h-[460px]">
                    <Image
                      src={panel.image.src}
                      alt={panel.image.alt}
                      fill
                      loading={index === 0 ? "eager" : "lazy"}
                      sizes="(min-width: 1280px) 420px, (min-width: 768px) 40vw, 88vw"
                      className="object-cover"
                      style={{ objectPosition: panel.imagePosition }}
                    />
                    <div
                      className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,5,5,0)_45%,rgba(5,5,5,0.2)_100%)]"
                      aria-hidden="true"
                    />
                  </figure>

                  <div className="flex min-h-[360px] flex-col p-5 sm:p-6 md:min-h-[460px] md:p-8">
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm font-black text-[#8c0000]">{panel.eyebrow}</p>
                      <p className="text-sm font-black text-[#050505]/38" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </p>
                    </div>

                    <h3 className="mt-7 text-[2.1rem] font-black leading-[1.14] text-[#050505] sm:text-[2.75rem] md:text-[3.35rem]">
                      {panel.titleLines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </h3>

                    <p className="mt-auto pt-8 text-base font-bold leading-8 text-[#4d3a00] md:text-lg md:leading-9">
                      {panel.copyLines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </p>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div className="mt-7 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            {panels.map((panel, index) => (
              <button
                key={panel.label}
                type="button"
                onClick={() => carouselApi?.scrollTo(index)}
                className={`size-2.5 rounded-full transition ${
                  currentSlide === index ? "bg-[#8c0000]" : "bg-[#050505]/18 hover:bg-[#050505]/32"
                }`}
                aria-label={`${index + 1}번째 회복 단계 보기`}
                aria-current={currentSlide === index ? "step" : undefined}
              />
            ))}
          </div>
          <p className="min-w-12 text-sm font-black text-[#4d3a00]" aria-live="polite">
            {currentSlide + 1}/{panels.length}
          </p>
        </div>
      </div>
    </section>
  );
}
